/**
 * Day-of vendor times offered to the timeline.
 *
 * A booking (the EVENT→VENDOR link) carries when the vendor arrives and when
 * they may load in, `HH:MM`. The timeline is the day's run-of-show; these
 * helpers say how one of those times reads as a timeline entry and when an
 * entry already says it.
 *
 * A timeline entry's `time` is free text: the editor writes `HH:MM` on the
 * 24-hour clock, older entries and imports hold `3:00 PM`, `3:00PM` or `9:05`.
 * `timelineTimeKey` reads all of them as one `HH:MM`, so "is it on the
 * timeline" never depends on how the time was typed.
 */
export const VENDOR_TIME_KINDS = ['arrival', 'loadIn'];
/** The section a vendor's times go to when the producer names none. */
export const VENDOR_TIMES_SECTION_NAME = 'Vendors';
const TWENTY_FOUR_HOUR = /^(\d{1,2}):(\d{2})$/;
const TWELVE_HOUR = /^(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]\.?$/;
/** A timeline time as `HH:MM` on the 24-hour clock; null when it reads as no time of day. */
export function timelineTimeKey(time) {
    if (typeof time !== 'string')
        return null;
    const text = time.trim();
    let h;
    let m;
    const plain = text.match(TWENTY_FOUR_HOUR);
    const twelve = plain ? null : text.match(TWELVE_HOUR);
    if (plain) {
        h = Number(plain[1]);
        m = Number(plain[2]);
    }
    else if (twelve) {
        h = Number(twelve[1]);
        m = Number(twelve[2]);
        if (h < 1 || h > 12)
            return null;
        const pm = twelve[3].toUpperCase() === 'P';
        if (pm && h < 12)
            h += 12;
        if (!pm && h === 12)
            h = 0;
    }
    else {
        return null;
    }
    if (h > 23 || m > 59)
        return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/** Minutes since midnight, for ordering a section by time; an entry with no time sorts last. */
export function timelineTimeMinutes(time) {
    const key = timelineTimeKey(time);
    if (!key)
        return Infinity;
    const [h, m] = key.split(':').map(Number);
    return h * 60 + m;
}
/** "Arrival" / "Load-in". */
export function vendorTimeKindLabel(kind) {
    return kind === 'arrival' ? 'Arrival' : 'Load-in';
}
/** The entry's title — "Bella Florals arrives", "Bella Florals load-in". Editable once it is on the timeline. */
export function vendorTimeTitle(vendorName, kind) {
    const name = vendorName.trim() || 'Vendor';
    return kind === 'arrival' ? `${name} arrives` : `${name} load-in`;
}
/** Whether a section is the one vendor times go to by default (its name, ignoring case and spaces). */
export function isVendorTimesSection(name) {
    return (name ?? '').trim().toLowerCase() === VENDOR_TIMES_SECTION_NAME.toLowerCase();
}
//# sourceMappingURL=vendorTimes.js.map